/**
 * Tech-debt #4 — Local Authorization List family: GetLocalListVersion and
 * SendLocalList (Full / Differential), persisted via the LAL repos.
 */
import {
  applyUpdate as applyAuthListUpdate,
  MAX_ID_TAG_LENGTH_V16,
  MAX_ID_TAG_LENGTH_V201,
} from '@/domain/LocalAuthList';
import type { LocalAuthListEntryRow } from '@/persistence/ports';
import { OcppV201Adapter } from '@/protocol/v201/OcppV201Adapter';
import type { CsmsCtx, CsmsEvent } from './context';
import { localAuthListRepo, localAuthListVersionRepo } from './shared';

export async function handleGetLocalListVersion(
  _event: CsmsEvent<'GetLocalListVersion'>,
  ctx: CsmsCtx,
): Promise<void> {
  const row = await localAuthListVersionRepo.get(ctx.stationId);
  const version = row?.listVersion ?? 0;
  // Session 19a — v2.0.1 uses `versionNumber`; v1.6 uses `listVersion`.
  const isV201 = ctx.adapter instanceof OcppV201Adapter;
  ctx.dispatcher.reply(
    ctx.messageId,
    isV201 ? { versionNumber: version } : { listVersion: version },
  );
}

export async function handleSendLocalList(
  event: CsmsEvent<'SendLocalList'>,
  ctx: CsmsCtx,
): Promise<void> {
  const { adapter, dispatcher, stationId, messageId } = ctx;
  const current = await localAuthListRepo.byStation(stationId);
  // Session 19a — v2.0.1 IdToken is ≤ 36 chars vs v1.6's ≤ 20.
  const maxIdLength =
    adapter instanceof OcppV201Adapter ? MAX_ID_TAG_LENGTH_V201 : MAX_ID_TAG_LENGTH_V16;
  const result = applyAuthListUpdate(
    current.map((r) => ({
      idTag: r.idTag,
      status: r.status,
      ...(r.expiryDate !== undefined ? { expiryDate: r.expiryDate } : {}),
      ...(r.parentIdTag !== undefined ? { parentIdTag: r.parentIdTag } : {}),
    })),
    event.updateType,
    event.localAuthorizationList,
    maxIdLength,
  );
  if (!result.ok) {
    dispatcher.reply(messageId, { status: 'Failed' });
    return;
  }
  const now = Date.now();
  const nextRows: LocalAuthListEntryRow[] = result.next.map((e) => ({
    stationId,
    idTag: e.idTag,
    status: e.status,
    ...(e.expiryDate !== undefined ? { expiryDate: e.expiryDate } : {}),
    ...(e.parentIdTag !== undefined ? { parentIdTag: e.parentIdTag } : {}),
    updatedAt: now,
  }));
  try {
    await localAuthListRepo.replaceAll(stationId, nextRows);
    await localAuthListVersionRepo.set(stationId, event.listVersion);
  } catch {
    dispatcher.reply(messageId, { status: 'Failed' });
    return;
  }
  dispatcher.reply(messageId, { status: 'Accepted' });
}
